import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEdgesState, useNodesState } from 'reactflow';

import { MAIN_NODE_WIDTH, NODE_WIDTH } from 'modules/devices/styles/device-dashboard';

import { differenceInDays } from 'date-fns';
import { formatUTCDate, getTeamPath } from 'helpers';
import { getResponseData } from 'helpers/response';
import { apiCall } from 'hooks/service/api';
import { compact, sortBy } from 'lodash';

import { LexikonTeamId } from 'constants/index';
import { PATH } from 'constants/path';

import { type DeviceDetail, type DeviceRecord } from '..';

export const useGlobalDeviceDashboardController = () => {
  const { id: teamId } = useParams();
  const navigationTo = useNavigate();
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);

  const onRedirectDevicePage = () => {
    navigationTo(getTeamPath(teamId || '', PATH.TEAM_DEVICES));
  };

  useEffect(() => {
    if (!teamId) return;

    const fetchDevicesAndBuildGraph = async () => {
      const deviceListRes: { data: DeviceDetail[] } = await apiCall({
        url: `/api/teams/${teamId}/devices?with_label=1`,
        method: 'GET',
        isLoading: true,
        showError: true,
      });

      const allDevices = getResponseData(deviceListRes?.data) || [];
      const allNodes: any[] = [];
      const allEdges: any[] = [];
      let lastCountChildNodes = 0;
      for (const [deviceIndex, device] of allDevices.entries()) {
        // Filter Lexikon labels if needed
        let deviceLabels = device?.labels || [];
        if (teamId === LexikonTeamId) {
          const allowed = ['dev 1', 'dev 2', 'dev 3'];
          deviceLabels = deviceLabels.filter((l) => allowed.includes(l.label.toLowerCase()));
        }
        deviceLabels = sortBy(deviceLabels, ['label']);
        if (deviceLabels.length === 0) continue;

        // Get all records for this device
        const recRes: { data: DeviceRecord[] } = await apiCall({
          url: `/api/teams/${teamId}/devices/${device.id}/records`,
          data: {
            sort: 'created_at',
            order: 'asc',
            filter: JSON.stringify({
              data_type: 'text',
              data: { $regex: 'Detected', $options: 'i' },
              label: { $in: deviceLabels.map((l) => l.label) },
            }),
          },
          method: 'GET',
          isLoading: false,
          showError: false,
        });

        const records = getResponseData(recRes?.data) || [];

        // Get latest records per label
        const latestPerLabelPromises = deviceLabels.map(async (label) => {
          const res: { data: DeviceRecord[] } = await apiCall({
            url: `/api/teams/${teamId}/devices/${device.id}/records`,
            data: {
              sort: 'created_at',
              order: 'desc',
              page: 1,
              page_size: 1,
              filter: JSON.stringify({ label: label.label }),
            },
            method: 'GET',
            isLoading: false,
            showError: false,
          });
          return getResponseData(res?.data)?.[0];
        });

        const latestRecords = compact(await Promise.all(latestPerLabelPromises));

        const labelRecords = deviceLabels.map((label) => ({
          label: label.label,
          records: records.filter((r) => r.label === label.label),
        }));

        const deviceNodeId = `device-${device.id}`;
        const childNodes = labelRecords.map((rec, index) => ({
          id: `${deviceNodeId}-child-${rec.label}`,
          type: 'childNode',
          data: {
            label: rec.label,
            logs: rec.records.slice(-10).map((r) => {
              return (
                formatUTCDate(r.created_at) +
                '/' +
                (r.data.toLowerCase().includes('not detected') ? 'Not detected' : ' Detected')
              );
            }),
            detected: latestRecords.some(
              (l) =>
                l.label === rec.label &&
                !l.data.toLowerCase().includes('not detected') &&
                differenceInDays(Date.now(), new Date(l.created_at)) === 0,
            ),
          },
          position: {
            x: deviceIndex * lastCountChildNodes * 333 + index * NODE_WIDTH,
            y: 300,
          },
        }));

        const mainDetected = childNodes.some((n) => n.data.detected);
        lastCountChildNodes = childNodes.length;
        allNodes.push({
          id: deviceNodeId,
          type: 'mainNode',
          position: {
            x:
              deviceIndex * childNodes.length * 333 +
              (childNodes.length * NODE_WIDTH - MAIN_NODE_WIDTH) / 2,
            y: 0,
          },
          data: {
            label: device.alias || device.id,
            detected: mainDetected,
          },
        });

        allNodes.push(...childNodes);

        allEdges.push(
          ...childNodes.map((child, i) => {
            return {
              id: `edge-${device.id}-${i}`,
              source: deviceNodeId,
              target: child.id,
              type: 'smoothstep',
              className: 'edge-line',
              selectable: false,
              animated: true, //child.data.detected,
              selected: child.data.detected,
              focusable: false,
            };
          }),
        );
      }

      setNodes(allNodes);
      setEdges(allEdges);
    };

    fetchDevicesAndBuildGraph();
  }, [teamId]);

  return { nodes, edges, onRedirectDevicePage };
};
